/**
 * Stripe Payment Form — deferred intent flow
 *
 * Card form renders immediately without a clientSecret (using mode: 'payment').
 * On submit: validate card -> create order on backend -> confirm payment.
 */

import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { Lock, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui'
import { useTheme } from '@/context/ThemeContext'
import { getStripeAppearance } from '@/utils/stripeAppearance'

interface StripePaymentFormProps {
  onCreateOrder: () => Promise<{ clientSecret: string; orderId?: string } | null>
  onSuccess: () => void
  onError: (message: string) => void
  isProcessing: boolean
  setIsProcessing: (v: boolean) => void
  total: number
  currency?: string
  disabled?: boolean
  isCreatingOrder?: boolean
  checkoutStep?: string
}

export function StripePaymentForm({
  onCreateOrder,
  onSuccess,
  onError,
  isProcessing,
  setIsProcessing,
  total,
  currency = 'USD',
  disabled,
  isCreatingOrder,
  checkoutStep,
}: StripePaymentFormProps) {
  const { t } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const submittingRef = useRef(false)

  // Sync Stripe appearance with dark/light mode changes
  const { theme: appTheme } = useTheme()
  useEffect(() => {
    if (!elements) return
    elements.update({ appearance: getStripeAppearance(appTheme === 'dark') })
  }, [elements, appTheme])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements || submittingRef.current) return

    submittingRef.current = true
    setIsProcessing(true)
    setPaymentError(null)

    // Step 1: Validate card details
    const { error: submitError } = await elements.submit()
    if (submitError) {
      setPaymentError(submitError.message || t('common:checkCardDetails'))
      submittingRef.current = false
      setIsProcessing(false)
      return
    }

    // Step 2: Create order on backend -> get clientSecret
    const result = await onCreateOrder()
    if (!result?.clientSecret) {
      setPaymentError(t('common:couldNotCreateOrder'))
      submittingRef.current = false
      setIsProcessing(false)
      return
    }

    // Step 3: Confirm payment with Stripe
    // In iframe mode, use the widget's own origin (not the embedder's)
    const widgetOrigin = new URL(window.location.href).origin
    const returnUrl = widgetOrigin + '/tracking' + (result.orderId ? `?order_id=${result.orderId}` : '')
    const { error } = await stripe.confirmPayment({
      elements,
      clientSecret: result.clientSecret,
      confirmParams: {
        return_url: returnUrl,
      },
      redirect: 'if_required',
    })

    if (error) {
      const message = error.message || 'Payment failed. Please try again.'
      setPaymentError(message)
      onError(message)
      submittingRef.current = false
      setIsProcessing(false)
    } else {
      submittingRef.current = false
      setIsProcessing(false)
      onSuccess()
    }
  }

  const formattedTotal = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(total / 100)

  const showCreatingSteps = isCreatingOrder && checkoutStep

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t('common:payment')}</h2>
        <p className="text-sm text-muted-foreground flex items-center gap-1">
          <Lock className="h-3.5 w-3.5" />
          {t('common:securePaymentViaStripe')}
        </p>
      </div>

      <div className="border rounded-lg p-4">
        <PaymentElement options={{
          layout: 'accordion',
          wallets: { applePay: 'never', googlePay: 'never', link: 'never' },
        }} />
      </div>

      {paymentError && (
        <p className="text-sm text-destructive">{paymentError}</p>
      )}

      <Button
        type="submit"
        variant="black"
        size="lg"
        disabled={!stripe || !elements || isProcessing || disabled || isCreatingOrder}
        className="w-full"
      >
        {showCreatingSteps ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            {checkoutStep === 'authenticating' && t('common:signingIn')}
            {checkoutStep === 'syncing-cart' && t('common:syncingCart')}
            {checkoutStep === 'validating' && t('common:validatingOrder')}
            {checkoutStep === 'processing' && t('common:creatingOrder')}
          </>
        ) : isProcessing ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            {t('common:processingPayment')}
          </>
        ) : (
          t('common:payAmount', { amount: formattedTotal })
        )}
      </Button>
    </form>
  )
}
